import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Label } from "@admin/components/atoms/Input/Label";
import { PasswordInput } from "@admin/components/atoms/Input/PasswordInput";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  passwordValidation,
  Personnel,
  repasswordValidation,
  updateUserPassword,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditPasswordModalProps extends ModalProps {
  personnel?: Personnel;
}

export default function EditPasswordModal({
  personnel,
  ...props
}: EditPasswordModalProps) {
  const { t } = useTranslation();

  const passwordSchema = z
    .object({
      password: passwordValidation(t),
      passwordAgain: repasswordValidation(t),
    })
    .refine((data) => data.password === data.passwordAgain, {
      message: t("common.Passwords must match"),
      path: ["passwordAgain"],
    });
  type PasswordData = z.infer<typeof passwordSchema>;
  const {
    handleSubmit,
    register,
    reset,
    formState: state,
  } = useForm<{
    password: string;
    passwordAgain: string;
  }>({
    resolver: zodResolver(passwordSchema),
  });

  const handleClose = () => {
    reset();
    props.onClose?.();
  };

  const mutation = useMutation({
    mutationFn: updateUserPassword,
    onSuccess: () => {
      props.onClose?.();
      reset();
      toast.success(t("personnel.The password has been changed"));
    },
    onError: () => {
      toast.error(t("personnel.Unable to change the password"));
    },
  });
  const onSubmit = (data: PasswordData) => {
    if (personnel) {
      mutation.mutate({
        userId: personnel?.userId,
        password: data.password,
      });
    }
  };
  return (
    <Modal
      title={t("personnel.Edit Personnel Password", {
        personnelName: personnel?.fullName,
      })}
      actionButtons={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            {t("common.Save Changes")}
          </Button>
        </>
      }
      {...props}
    >
      <form className="flex flex-col gap-2.5">
        <div className="flex flex-col">
          <Label title={t("common.New Password")} required>
            <PasswordInput
              {...register("password")}
              placeholder={t("common.Enter Password")}
              name="password"
            />
          </Label>
          <ErrorMessage message={state.errors?.password?.message} />
        </div>
        <div className="flex flex-col">
          <Label title={t("common.New Password Again")} required>
            <PasswordInput
              {...register("passwordAgain")}
              placeholder={t("common.Enter Password Again")}
              name="passwordAgain"
            />
          </Label>
          <ErrorMessage message={state.errors?.passwordAgain?.message} />
        </div>
      </form>
    </Modal>
  );
}
